import { useEffect, useMemo, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { BarStack } from "@visx/shape";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { AnimatedBar, ChartCard, Skeleton } from "@/components/reusable";
import { fetchSumsInsuredByType } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { SiByTypeDatum } from "@/types/analytics";

type BasisKey = "hmFullUsd" | "tloUsd";
const KEYS: BasisKey[] = ["hmFullUsd", "tloUsd"];
const KEY_LABELS: Record<BasisKey, string> = {
  hmFullUsd: "H&M full conditions",
  tloUsd: "TLO only",
};
const KEY_COLOURS: Record<BasisKey, string> = {
  hmFullUsd: "#185FA5",
  tloUsd: "#5DCAA5",
};

function fmtAxisUsd(v: number): string {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${Math.round(v / 1e6)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${v}`;
}

export function SiByTypeStackedBar() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<SiByTypeDatum[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchSumsInsuredByType(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  return (
    <ChartCard
      title="Sums insured by vessel type"
      subtitle="Agreed values, USD · stacked by cover basis"
      headerBelow={
        <div className="flex gap-3 flex-wrap">
          {KEYS.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)]"
            >
              <span
                className="w-2.5 h-2.5 rounded-[2px]"
                style={{ background: KEY_COLOURS[k] }}
                aria-hidden="true"
              />
              {KEY_LABELS[k]}
            </span>
          ))}
        </div>
      }
      minHeight={340}
    >
      {!data ? (
        <Skeleton height="h-full" className="min-h-[200px]" />
      ) : (
        <div className="h-full w-full">
          <ParentSize>
            {({ width, height }) =>
              width > 0 && height > 0 ? (
                <StackedBarChart data={data} width={width} height={height} />
              ) : null
            }
          </ParentSize>
        </div>
      )}
    </ChartCard>
  );
}

interface ChartProps {
  data: SiByTypeDatum[];
  width: number;
  height: number;
}

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid rgba(0,0,0,0.14)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
};

function StackedBarChart({ data, width, height }: ChartProps) {
  const margin = { top: 10, right: 10, bottom: 26, left: 50 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const totals = useMemo(() => data.map((d) => d.hmFullUsd + d.tloUsd), [data]);
  const maxTotal = Math.max(1, ...totals);

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        range: [0, innerW],
        domain: data.map((d) => d.typeLabel),
        padding: 0.3,
      }),
    [data, innerW]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [innerH, 0],
        domain: [0, maxTotal * 1.1],
        nice: true,
      }),
    [innerH, maxTotal]
  );

  const colorScale = useMemo(
    () => scaleOrdinal<BasisKey, string>({ domain: KEYS, range: KEYS.map((k) => KEY_COLOURS[k]) }),
    []
  );

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<{ label: string; key: BasisKey; value: number }>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left}>
          <BarStack<SiByTypeDatum, BasisKey>
            data={data}
            keys={KEYS}
            x={(d) => d.typeLabel}
            xScale={xScale}
            yScale={yScale}
            color={colorScale}
          >
            {(stacks) =>
              stacks.map((stack, stackIdx) =>
                stack.bars.map((bar) => (
                  <AnimatedBar
                    key={`bar-${stack.key}-${bar.index}`}
                    x={bar.x}
                    width={bar.width}
                    targetY={bar.y}
                    targetHeight={bar.height}
                    baselineY={innerH}
                    fill={bar.color}
                    rx={stack.key === "tloUsd" ? 3 : 0}
                    delayMs={bar.index * 40 + stackIdx * 120}
                    durationMs={650}
                    onMouseMove={(e) => {
                      const svg = e.currentTarget.ownerSVGElement as SVGSVGElement | null;
                      const pt = svg?.getBoundingClientRect();
                      const left = pt ? e.clientX - pt.left : bar.x + bar.width / 2;
                      const top = pt ? e.clientY - pt.top : bar.y;
                      showTooltip({
                        tooltipData: {
                          label: bar.bar.data.typeLabel,
                          key: stack.key,
                          value:
                            stack.key === "hmFullUsd"
                              ? bar.bar.data.hmFullUsd
                              : bar.bar.data.tloUsd,
                        },
                        tooltipLeft: left,
                        tooltipTop: top,
                      });
                    }}
                    onMouseLeave={hideTooltip}
                  />
                ))
              )
            }
          </BarStack>
          <AxisBottom
            top={innerH}
            scale={xScale}
            stroke="transparent"
            tickStroke="transparent"
            hideAxisLine
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "middle",
              fontFamily: "var(--font-sans)",
            })}
          />
          <AxisLeft
            scale={yScale}
            numTicks={5}
            tickFormat={(v) => fmtAxisUsd(Number(v))}
            stroke="transparent"
            tickStroke="transparent"
            hideAxisLine
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "end",
              dx: -4,
              dy: 3,
              fontFamily: "var(--font-sans)",
            })}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="font-medium">{tooltipData.label}</div>
          <div className="text-[var(--color-text-secondary)]">
            {KEY_LABELS[tooltipData.key]}: {fmtAxisUsd(tooltipData.value)}
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
}
